export const RTBloggerControllerFactory = ({
  rtBloggerService,
}: {
  rtBloggerService: IRTBloggerService;
}) => {
  const postParseRTAR: IPostParseRTAR = async ({ file, clinicId }) => {
    const response = await rtBloggerService.postParseRTARService({
      file,
      clinic_id: clinicId,
    });

    return response;
  };

  const getBillingSettings: IGetBillingSettings = async (payload) => {
    const response = await rtBloggerService.getBillingSettingsService(payload);

    return response;
  };

  const updateBillingSettings: IUpdateBillingSettings = async (payload) => {
    const response = await rtBloggerService.updateBillingSettingsService(
      payload
    );

    return response;
  };

  const getServices: IGetServices = async (payload) => {
    const response = await rtBloggerService.getServicesService(payload);

    return response;
  };

  const updateServices: IUpdateServices = async (payload) => {
    const response = await rtBloggerService.updateServicesService(payload);

    return response;
  };

  const getAuditSetting: IGetAudit = async (payload) => {
    const response = await rtBloggerService.getAuditService(payload);

    return response;
  };

  const updateAudit: IUpdateAudit = async (payload) => {
    const response = await rtBloggerService.updateAuditService(payload);

    return response;
  };

  const postRTARBilling: IPostRTARBilling = async (payload) => {
    const response = await rtBloggerService.postRTARBillingService({
      services: payload.services,
      clinic_id: payload.clinicId,
      patient_id: payload.patientId,
      month: payload.month,
      year: payload.year,
    });

    return response;
  };

  const getCPTTypes: IGetCPTTypes = async () => {
    const response = await rtBloggerService.getCPTTypesService();

    return response;
  };

  const getEnrolledPatients: IGetEnrolledPatients = async (payload) => {
    const response = await rtBloggerService.getEnrolledPatientsService(payload);

    if (response.success && response.data) {
      const patientListEnriched = response.data.map((patient) => {
        let status;
        if (patient.rtx_enrolment_status === 1) {
          if (new Date(patient.program_end_date) < new Date()) {
            status = "Expired";
          } else {
            status = "Enrolled";
          }
        } else if (patient.rtx_enrolment_status === 2) {
          status = "Discharged";
        } else if (patient.rtx_enrolment_status === 3) {
          status = "Monitored";
        } else if (
          patient.rtx_enrolment_id !== null &&
          patient.rtx_enrolment_status === 0
        ) {
          status = "Pending";
        } else {
          status = "Not Enrolled";
        }

        return {
          ...patient,
          status_enriched: status,
        };
      });
      const discoveryPatients = patientListEnriched.filter(
        (item) =>
          item.status_enriched === "Not Enrolled" ||
          item.status_enriched === "Pending" ||
          item.status_enriched === "Discharged" ||
          item.status_enriched === "Monitored"
      );
      const patientFiltered = patientListEnriched
        .filter(
          (item) =>
            item.status_enriched !== "Not Enrolled" &&
            item.status_enriched !== "Discharged"
        )
        .sort((a, b) => {
          const order: Record<string, number> = {
            Expired: 0,
            Enrolled: 1,
            Pending: 2,
            Monitored: 3,
          };
          return order[a.status_enriched] - order[b.status_enriched];
        });

      return {
        success: true,
        data: { enrolledPatients: patientFiltered, discoveryPatients },
      };
    } else {
      return {
        success: false,
        data: { enrolledPatients: [], discoveryPatients: [] },
      };
    }
  };

  const putBillingServiceStatus: IPutBillingServiceStatus = async (payload) => {
    const response = await rtBloggerService.putBillingServiceStatusService({
      clinic_id: payload.clinicId,
      patient_id: payload.patientId,
      month: payload.month,
      year: payload.year,
      status: payload.status,
      service_log: payload.serviceLog,
      billing_log: payload.billingLog,
      admin: payload.admin,
      patient_status: payload.patientStatus,
    });

    return response;
  };

  const getOverviewPatients: IGetOverviewPatients = async (payload) => {
    const response = await rtBloggerService.getOverviewPatientsService(payload);
    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
      };
    } else {
      return {
        success: false,
      };
    }
  };

  const getBillingCycle: IGetBillingCycle = async (payload) => {
    const response = await rtBloggerService.getBillingCycleService(payload);

    return response;
  };

  const getBillingCycleForPatient: IGetBillingCycleForPatient = async (
    payload
  ) => {
    const response = await rtBloggerService.getBillingCycleForPatientService(
      payload
    );

    return response;
  };

  const deleteBillingCycle: IDeleteBillingCycle = async (payload) => {
    const response = await rtBloggerService.deleteBillingCycleService({
      clinic_id: payload.clinicId,
      month: payload.month,
      year: payload.year,
      current_status: payload.currentStatus,
    });

    return response;
  };

  const putBillingCycleStatusForPatient: IPutBillingCycleStatusForPatient =
    async ({ clinicId, patientId, month, year, newStatus }) => {
      const response =
        await rtBloggerService.putBillingCycleStatusForPatientService({
          clinic_id: clinicId,
          patient_id: patientId,
          month,
          year,
          new_status: newStatus,
        });

      return response;
    };

  const deleteBillingCycleForPatient: IDeleteBillingCycleForPatient = async ({
    clinicId,
    patientId,
    month,
    year,
  }) => {
    const response = await rtBloggerService.deleteBillingCycleForPatientService(
      {
        clinic_id: clinicId,
        patient_id: patientId,
        month,
        year,
      }
    );

    return response;
  };

  const postBillingFiles: IPostBillingFiles = async ({
    clinicId,
    patientIds,
    month,
    year,
  }) => {
    const response = await rtBloggerService.postBillingFilesService({
      clinic_id: clinicId,
      patient_ids: patientIds,
      month,
      year,
    });

    return response;
  };

  const putEnrollPatient: IPutEnrollPatient = async (payload) => {
    const response = await rtBloggerService.putEnrollPatientService(payload);

    return response;
  };

  const postExtendEnrollPatient: IPostExtendEnrollPatient = async (payload) => {
    const response = await rtBloggerService.postExtendEnrollPatientService(
      payload
    );

    return response;
  };

  const getBillingReports: IGetBillingReports = async (payload) => {
    const response = await rtBloggerService.getBillingReportsService(payload);
    if (response.success && response.data && Array.isArray(response.data)) {
      return {
        success: true,
        data: response.data,
      };
    } else {
      return {
        success: false,
        message: "No data found",
      };
    }
  };

  const updateBillingReportCollectedAmount: IUpdateBillingReportCollectedAmount =
    async (payload) => {
      const response =
        await rtBloggerService.updateBillingReportCollectedAmountService(
          payload
        );

      return response;
    };

  const getPreviouslyEnrolledPatients: IGetPreviouslyEnrolledPatients = async (
    payload
  ) => {
    const response =
      await rtBloggerService.getPreviouslyEnrolledPatientsService(payload);

    return response;
  };

  const postFixError: IPostFixError = async ({
    clinicId,
    patientId,
    month,
    year,
    serviceLog,
    auditLog,
  }) => {
    const response = await rtBloggerService.postFixErrorService({
      clinic_id: clinicId,
      patient_id: patientId,
      month,
      year,
      service_log: serviceLog,
      audit_log: auditLog,
    });

    return response;
  };

  return {
    postParseRTAR,
    getBillingSettings,
    updateBillingSettings,
    getServices,
    updateServices,
    getAuditSetting,
    updateAudit,
    postRTARBilling,
    getCPTTypes,
    getEnrolledPatients,
    putBillingServiceStatus,
    getOverviewPatients,
    getBillingCycle,
    getBillingCycleForPatient,
    deleteBillingCycle,
    putBillingCycleStatusForPatient,
    deleteBillingCycleForPatient,
    postBillingFiles,
    putEnrollPatient,
    postExtendEnrollPatient,
    getBillingReports,
    updateBillingReportCollectedAmount,
    getPreviouslyEnrolledPatients,
    postFixError,
  };
};
